import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { toPromoZmSearch } from '@/features/promotion/utils/promotionFilters';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange, type DatePeriod } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { MpReportSearch } from '../types/marketPromotion.types';
import {
  toMpReportFilterSearch,
  toMpReportFilterValues,
  toMpSummaryFilterSearch,
  toMpSummaryFilterValues,
} from '../utils/mpFilters';

const adjustmentRoute = getRouteApi('/_auth/market-promotion/adjustment/');
const summaryRoute = getRouteApi('/_auth/market-promotion/distribution-summary');

type FilterValues = Record<string, string>;

interface ReportFilterMappers<TFilterSearch> {
  toValues: (search: TFilterSearch, defaultPeriod: DatePeriod) => FilterValues;
  toSearch: (values: FilterValues) => TFilterSearch;
}

function useMpReportFilters<TSearch extends MpReportSearch, TFilterSearch>(
  search: TSearch & TFilterSearch,
  mappers: ReportFilterMappers<TFilterSearch>,
  applySearch: (nextSearch: TFilterSearch | { search: string | undefined }) => void,
) {
  const { toValues, toSearch } = mappers;
  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(
    () => toValues(search, defaultPeriod),
    [toValues, search, defaultPeriod],
  );

  const normalizeFilterValues = useCallback(
    (values: FilterValues) => toValues(toSearch(values), defaultPeriod),
    [toValues, toSearch, defaultPeriod],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => applySearch(toSearch(values)),
  });

  const searchBox = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => applySearch({ search: text }),
  });

  const params = useMemo(
    (): TSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  return { ...filters, ...searchBox, params };
}

const ADJUSTMENT_MAPPERS = { toValues: toMpReportFilterValues, toSearch: toMpReportFilterSearch };
const SUMMARY_MAPPERS = { toValues: toMpSummaryFilterValues, toSearch: toMpSummaryFilterSearch };

export function useMpAdjustmentFilters() {
  const search = adjustmentRoute.useSearch();
  const navigate = adjustmentRoute.useNavigate();

  const filters = useMpReportFilters(search, ADJUSTMENT_MAPPERS, (nextSearch) => {
    void navigate({
      search: (previous) => ({ ...previous, ...nextSearch }),
      replace: true,
    });
  });

  const drillParams = useMemo(() => toPromoZmSearch(filters.params), [filters.params]);

  return { ...filters, drillParams };
}

export function useMpSummaryFilters() {
  const search = summaryRoute.useSearch();
  const navigate = summaryRoute.useNavigate();

  return useMpReportFilters(search, SUMMARY_MAPPERS, (nextSearch) => {
    void navigate({
      search: (previous) => ({ ...previous, ...nextSearch }),
      replace: true,
    });
  });
}
